import { useState } from 'react'

import { DateRangePicker, type IDateRangePickerProps } from './DateRangePicker'
import type { DateValue, FocusedRangeInput } from './types'

export interface IUncontrolledDateRangePickerProps extends Omit<
  IDateRangePickerProps,
  | 'startDate'
  | 'endDate'
  | 'startDateId'
  | 'endDateId'
  | 'focusedInput'
  | 'onDatesChange'
  | 'onFocusChange'
> {
  /** Initial/controlled start date */
  startDate?: DateValue
  /** Initial/controlled end date */
  endDate?: DateValue
  /** Field name (used for generating input IDs) */
  name?: string
  /** Called when both dates are selected */
  onChange?: (value: { startDate: Date; endDate: Date }) => void
  /** Full-width display */
  fullWidth?: boolean
  /** Optional override for start date input ID */
  startDateId?: string
  /** Optional override for end date input ID */
  endDateId?: string
}

export const UncontrolledDateRangePicker = ({
  startDate: startDateProp,
  endDate: endDateProp,
  name = 'daterange',
  onChange = () => undefined,
  fullWidth,
  startDateId,
  endDateId,
  ...rest
}: IUncontrolledDateRangePickerProps) => {
  const [dateValue, setDateValue] = useState<{
    focusedInput: FocusedRangeInput
    startDate: DateValue
    endDate: DateValue
  }>({
    focusedInput: null,
    startDate: null,
    endDate: null,
  })

  return (
    <DateRangePicker
      startDate={startDateProp ?? dateValue.startDate}
      startDateId={startDateId ?? `${name}_0`}
      endDate={endDateProp ?? dateValue.endDate}
      endDateId={endDateId ?? `${name}_1`}
      focusedInput={dateValue.focusedInput}
      block={fullWidth}
      onDatesChange={({ startDate: newStart, endDate: newEnd }) => {
        setDateValue((state) => ({
          ...state,
          startDate: newStart,
          endDate: newEnd,
        }))

        if (newStart && newEnd) {
          onChange({ startDate: newStart, endDate: newEnd })
        }
      }}
      onFocusChange={(focusedInput) => {
        setDateValue((state) => ({
          ...state,
          focusedInput,
        }))

        if (focusedInput === null) {
          setDateValue((state) => {
            if (!state.startDate || !state.endDate) {
              return { startDate: null, endDate: null, focusedInput }
            }
            return { ...state, focusedInput }
          })
        }
      }}
      {...rest}
    />
  )
}

UncontrolledDateRangePicker.displayName = 'UncontrolledDateRangePicker'
